import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  getLieuxPage,
  OngletLieux,
  getById,
  getByPlacekey,
  updateDeleteLocation,
  updateLocation,
  updateLocationByPlacekey,
  getMotifsRejet,
  getDecisions,
  analyserVision,
} from "../../utilities/locationService";
import { getUser } from "../../utilities/adminservice";
import { messageServeur } from "../../utilities/userService";
import { loaderOff, loaderOn, openSnackbar } from "../../redux/actions";

/* ══════════════════════════════════════════════════════════════════════════
   Logique de la page Lieux, extraite telle quelle de pages/location.tsx pour
   être partagée par la carte des lieux, la file « Lieux à valider » et
   l'Accueil. Mêmes appels, mêmes paramètres, mêmes contrôles (motif de rejet
   obligatoire, commentaire exigé par certains motifs, motif de masquage,
   droit location_write) : seule la présentation change dans les écrans.
   ══════════════════════════════════════════════════════════════════════════ */

export interface Data {
  rowIndex: number;
  User: string;
  Category: string;
  Location_Name: string;
  Location_Address: string;
  IMG_Attached: string;
  Token: number;
  Status: string;
  /** Écart GPS EXIF de la photo / position déclarée (signauxExif), null sans GPS. */
  photo_distance?: number | null;
  /** Écart dans le seuil serveur (signauxExif.gpsConforme), null sans GPS. */
  photo_conforme?: boolean | null;
  photo_timestamp?: string | null;
  referenceId: string;
  placekey?: string;
  createdBy?: string;
  /** Nombre de corroborations (autres Spotters à moins de 20 m) : la vraie double validation. */
  nbCorroborations: number;
  latitude?: number | null;
  longitude?: number | null;
  createdOn?: string | number | null;
  /** Tips qui seront réellement crédités à l'approbation (découvreur + corroborateurs non encore payés). */
  aCrediter?: number;
  /** Lieu approuvé dont le crédit n'a pas été confirmé par user-service (relançable). */
  creditEchec?: boolean;
  creditEchecMessage?: string | null;
  creditEchecLe?: string | number | null;
  /** Verdict Vision de la liste (conforme | douteux | non_conforme), null si non analysé. */
  verdict?: string | null;
}

export const toDataRow = (r: any, index: number): Data => {
  const resolvedId = r.referenceId || r.placekey || r.id || r._id || null;
  const row: any = {
    verdict: r.verdictVision?.verdict ?? null,
    rowIndex: index,
    User: "" + (index + 1),
    Category: r.locationType,
    Location_Name: r.locationName,
    Location_Address: r.address,
    IMG_Attached: r.uploads && r.uploads.length > 0 ? "Yes" : "No",
    Token: r.token,
    Status: r.status,
    photo_distance: r.signauxExif?.ecartMetres ?? null,
    photo_conforme: r.signauxExif?.gpsConforme ?? null,
    photo_timestamp: r.signauxExif?.date ?? null,
    referenceId: resolvedId,
    placekey: r.placekey || null,
    createdBy: r.createdBy,
    nbCorroborations: Array.isArray(r.corroborations) ? r.corroborations.length : 0,
    latitude: r.latitude ?? null,
    longitude: r.longitude ?? null,
    createdOn: r.createdOn ?? null,
    // Fourni par le serveur pour la file ; recalculé ici pour une fiche ouverte depuis la carte.
    aCrediter: r.aCrediter ?? (r.credite ? 0 : Number(r.token) || 0) + (Array.isArray(r.corroborations)
      ? r.corroborations.filter((c: any) => !c.credite).reduce((t: number, c: any) => t + (Number(c.montantEnAttente) || 0), 0)
      : 0),
    creditEchec: r.creditEchec === true,
    creditEchecMessage: r.creditEchecMessage ?? null,
    creditEchecLe: r.creditEchecLe ?? null,
  };
  return row as Data;
};

export const ONGLETS: OngletLieux[] = ["attente", "verifie", "approuve", "rejete", "expire", "masque"];

/* Âge depuis la création (createdOn, Instant sérialisé en ISO ou en secondes
   epoch selon la config Jackson). */
export const enMillis = (v: string | number) => typeof v === "number" ? (v < 1e12 ? v * 1000 : v) : new Date(v).getTime();

/** « aujourd'hui », « il y a 3 j » ; null si la date est absente ou illisible. */
export function anciennete(v?: string | number | null): string | null {
  if (v == null) return null;
  const ms = enMillis(v);
  if (isNaN(ms)) return null;
  const jours = Math.floor((Date.now() - ms) / 86400000);
  if (jours <= 0) return "aujourd'hui";
  if (jours === 1) return "hier";
  return `il y a ${jours} j`;
}

export function statusColor(status: string): string {
  const s = (status ?? "").toLowerCase();
  // Convention commune aux cartes du back-office (CLAUDE.md).
  if (s === "approved" || s === "validated") return "#356A0F";
  if (s === "pending" || s === "en_attente") return "#7C5600";
  if (s === "rejected" || s === "rejeté") return "#A23A0D";
  return "#57534A";
}

export interface MotifRejet { code: string; libelle: string; commentaireObligatoire: boolean }

// ═══════════════════════════════════════════════════════════════════════════
// Fiche d'un lieu : lecture complète, approbation, rejet, analyse photo
// ═══════════════════════════════════════════════════════════════════════════

export function useFicheLieu(apresDecision: () => void) {
  const dispatch = useDispatch();
  const [location_write, setLocationWrite] = useState(false);

  useEffect(() => {
    getUser().then((res: any) => {
      if (res?.role_type) setLocationWrite(res.role_type.location_write ?? false);
    }).catch(() => dispatch(openSnackbar("Droits de l'administrateur illisibles : actions de modification masquées", "red")));
  }, []);

  const [openLocation, setOpenLocation]   = useState(false);
  const [referenceId, setReferenceId]     = useState<any>(null);
  const [images, setImages]               = useState<string[]>([]);
  const [imagesAvailable, setImagesAvailable] = useState(false);
  const [openImgModal, setOpenImgModal]   = useState(false);
  const [modalPhotoDistance, setModalPhotoDistance] = useState<number | null>(null);
  const [modalPhotoConforme, setModalPhotoConforme] = useState<boolean | null>(null);
  const [modalPhotoTimestamp, setModalPhotoTimestamp] = useState<string | null>(null);
  // Vague 1 : motif de rejet obligatoire (référentiel), commentaire, historique.
  const [motifs, setMotifs] = useState<MotifRejet[]>([]);
  const [motifRejet, setMotifRejet] = useState("");
  const [commentaireDecision, setCommentaireDecision] = useState("");
  const [decisions, setDecisions] = useState<any[]>([]);
  const [decisionsIndisponibles, setDecisionsIndisponibles] = useState(false);
  const [visionInfo, setVisionInfo] = useState<any>(null);
  const [analyseEnCours, setAnalyseEnCours] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Data | null>(null);
  const selectedLocationRef = useRef<Data | null>(null);
  const [formvalues, setFormvalues] = useState({
    status:       { value: "" },
    token:        { value: "" },
    address:      { value: "" },
    locationName: { value: "" },
    locationType: { value: "" },
    createdBy:    { value: "" },
  });

  useEffect(() => {
    getMotifsRejet()
      .then((m: any) => Array.isArray(m) && setMotifs(m))
      .catch(() => dispatch(openSnackbar("Référentiel des motifs de rejet indisponible : le rejet est impossible.", "red")));
  }, []);
  // Le POI lui-même (photos, Vision, EXIF) est lu une seule fois, dans ouvrirFiche.
  useEffect(() => {
    const loc: any = selectedLocation;
    setMotifRejet(""); setCommentaireDecision(""); setDecisions([]); setDecisionsIndisponibles(false);
    const id = loc && (loc.placekey || loc.referenceId);
    if (id) {
      getDecisions("POI", id)
        .then((d: any) => Array.isArray(d) && setDecisions(d))
        .catch(() => setDecisionsIndisponibles(true));
    }
  }, [selectedLocation]);

  /** Analyse photo à la demande ; renvoie le verdict (ou null). */
  const analyser = async (loc: any): Promise<any | null> => {
    const id = loc && (loc.referenceId || loc.placekey);
    if (!id) return null;
    let res: any;
    try {
      res = await analyserVision("POI", [id]);
    } catch (e) {
      dispatch(openSnackbar(messageServeur(e, "Analyse indisponible"), "red"));
      return null;
    }
    const r = res?.resultats?.[0];
    if (r?.verdict) return r.verdict;
    dispatch(openSnackbar(r?.erreur || "Analyse indisponible", "red"));
    return null;
  };

  const lancerAnalyse = async () => {
    const loc: any = selectedLocationRef.current || selectedLocation;
    if (!loc) return;
    setAnalyseEnCours(true);
    const verdict = await analyser(loc);
    setAnalyseEnCours(false);
    if (verdict) setVisionInfo((prev: any) => ({ verdict, exif: prev?.exif, integrite: prev?.integrite }));
  };

  const ouvrirFiche = async (row: Data) => {
    setSelectedLocation(row);
    selectedLocationRef.current = row;
    setFormvalues({
      status:       { value: row.Status ?? "" },
      token:        { value: String(row.Token ?? "") },
      address:      { value: row.Location_Address ?? "" },
      locationName: { value: row.Location_Name ?? "" },
      locationType: { value: row.Category ?? "" },
      createdBy:    { value: row.createdBy ?? "" },
    });
    setModalPhotoDistance(row.photo_distance ?? null);
    setModalPhotoConforme(row.photo_conforme ?? null);
    setModalPhotoTimestamp(row.photo_timestamp ?? null);
    setImages([]);
    setImagesAvailable(false);
    setVisionInfo(null);
    setReferenceId(row.referenceId);
    setOpenLocation(true); // ouvrir immédiatement, images chargées après

    if (row.placekey || row.referenceId) {
      try {
        const res = row.placekey
          ? await getByPlacekey(row.placekey)
          : await getById(row.referenceId);
        const d: any = Array.isArray(res) ? res[0] : (res?.data ?? res);
        if (d && row.aCrediter == null && selectedLocationRef.current === row) {
          // Ouvert depuis la carte (point léger) : la fiche complète donne Tips, corroborations et EXIF.
          const complet: Data = { ...row, ...toDataRow(d, row.rowIndex) };
          selectedLocationRef.current = complet;
          setFormvalues(f => ({ ...f, token: { value: String(complet.Token ?? "") } }));
          setModalPhotoDistance(complet.photo_distance ?? null);
          setModalPhotoConforme(complet.photo_conforme ?? null);
          setModalPhotoTimestamp(complet.photo_timestamp ?? null);
        }
        if (d) {
          const imgs = (d.images ?? []).map((img: any) => img.image_content);
          setImages(imgs);
          setImagesAvailable(imgs.length > 0);
          if (d.verdictVision || d.signauxExif || d.signauxIntegrite)
            setVisionInfo({ verdict: d.verdictVision, exif: d.signauxExif, integrite: d.signauxIntegrite });
        }
      } catch (e) {
        dispatch(openSnackbar(messageServeur(e, "Impossible de charger les photos et l'analyse de ce lieu."), "red"));
      }
    }
  };

  /** Approbation d'un lieu (crédit et notification faits par location-service, D5). */
  const approuverLieu = async (loc: Data | null): Promise<boolean> => {
    const jwt = sessionStorage.getItem("jwtToken");
    if (!jwt) { dispatch(openSnackbar("Session introuvable. Veuillez vous reconnecter.", "red")); return false; }
    if (!loc) { console.error("[approve] selectedLocation is null"); return false; }
    const token = Number(loc.Token ?? 0);
    const base = { locationType: loc.Category, locationName: loc.Location_Name, address: loc.Location_Address, token, status: "approved" };
    const payload = loc.placekey
      ? { ...base, placekey: loc.placekey }
      : { ...base, referenceId: loc.referenceId };
    dispatch(loaderOn());
    let ok = false;
    try {
      if (loc.placekey) await updateLocationByPlacekey(payload);
      else await updateLocation(payload);
      ok = true;
      apresDecision();
    } catch (e) {
      dispatch(openSnackbar(messageServeur(e, "Approbation refusée par le serveur : aucun Tips crédité."), "red"));
    }
    dispatch(loaderOff());
    return ok;
  };

  /** Rejet avec motif obligatoire du référentiel (notification faite par location-service, D5). */
  const rejeterLieu = async (loc: Data | null, motif: string, commentaire: string): Promise<boolean> => {
    const jwt = sessionStorage.getItem("jwtToken");
    if (!jwt) { dispatch(openSnackbar("Session introuvable. Veuillez vous reconnecter.", "red")); return false; }
    if (!loc) { console.error("[reject] selectedLocation is null"); return false; }
    // Motif obligatoire (le serveur refuse un rejet sans motif du référentiel).
    if (!motif) { dispatch(openSnackbar("Sélectionnez un motif de rejet", "red")); return false; }
    const motifDef = motifs.find(m => m.code === motif);
    if (motifDef?.commentaireObligatoire && !commentaire.trim()) {
      dispatch(openSnackbar("Ce motif exige un commentaire", "red")); return false;
    }
    const base = { locationType: loc.Category, locationName: loc.Location_Name, address: loc.Location_Address, token: Number(loc.Token ?? 0), status: "rejected", motifRejet: motif, commentaireDecision: commentaire };
    const payload = loc.placekey
      ? { ...base, placekey: loc.placekey }
      : { ...base, referenceId: loc.referenceId };
    dispatch(loaderOn());
    let ok = false;
    try {
      if (loc.placekey) await updateLocationByPlacekey(payload);
      else await updateLocation(payload);
      ok = true;
      apresDecision();
    } catch (e) {
      dispatch(openSnackbar(messageServeur(e, "Rejet refusé par le serveur."), "red"));
    }
    dispatch(loaderOff());
    return ok;
  };

  // Depuis la fiche : le lieu ouvert, le motif et le commentaire saisis dans la fiche.
  const handleUpdateLocationApproved = async () => {
    if (await approuverLieu(selectedLocationRef.current)) setOpenLocation(false);
  };
  const handleUpdateLocationRejected = async () => {
    if (await rejeterLieu(selectedLocationRef.current, motifRejet, commentaireDecision)) setOpenLocation(false);
  };

  return {
    location_write, motifs,
    openLocation, setOpenLocation, referenceId, images, imagesAvailable, openImgModal, setOpenImgModal,
    modalPhotoDistance, modalPhotoConforme, modalPhotoTimestamp,
    motifRejet, setMotifRejet, commentaireDecision, setCommentaireDecision,
    decisions, decisionsIndisponibles, visionInfo, analyseEnCours, lancerAnalyse, analyser,
    selectedLocation, selectedLocationRef, formvalues,
    ouvrirFiche, approuverLieu, rejeterLieu, handleUpdateLocationApproved, handleUpdateLocationRejected,
  };
}

export type FicheLieu = ReturnType<typeof useFicheLieu>;

// ═══════════════════════════════════════════════════════════════════════════
// File de validation : pagination serveur, compteurs, sélection, masquage
// ═══════════════════════════════════════════════════════════════════════════

export function useFileLieux(ongletInitial: number, apresDecisionExterne?: () => void) {
  const dispatch = useDispatch();
  const [rows, setRows] = useState<Data[]>([]);
  const [compteurs, setCompteurs] = useState<Record<OngletLieux, number>>({ attente: 0, verifie: 0, approuve: 0, rejete: 0, expire: 0, masque: 0 });
  const [totalOnglet, setTotalOnglet] = useState(0);
  const [valTab, setValTab]   = useState(ongletInitial);
  const [page, setPage]       = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [rechercheServeur, setRechercheServeur] = useState("");
  const [verdictFilter, setVerdictFilter] = useState("");   // filtre par verdict Vision
  const [verdictTri, setVerdictTri] = useState(false);      // trier douteux en premier
  const [chargement, setChargement] = useState(false);

  // La recherche part au serveur : on attend que la frappe se pose.
  useEffect(() => {
    const t = setTimeout(() => { setRechercheServeur(searchQuery.trim()); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  /* Z2-12 : la file est paginée par le serveur (POI non masqués, OSM compris,
     comme avant) ; compteurs et Tips à créditer viennent aussi du serveur. */
  const requeteListe = useRef(0);
  const fetchListe = async () => {
    const n = ++requeteListe.current;
    dispatch(loaderOn());
    setChargement(true);
    try {
      const res = await getLieuxPage({
        onglet: ONGLETS[valTab], recherche: rechercheServeur || undefined, verdict: verdictFilter || undefined,
        triDouteux: verdictTri, page, taille: rowsPerPage,
      });
      if (n !== requeteListe.current) return; // réponse dépassée par une requête plus récente
      setRows((res.contenu ?? []).map(toDataRow));
      setTotalOnglet(res.total ?? 0);
      if (res.compteurs) setCompteurs(res.compteurs);
    } catch (error) {
      if (n === requeteListe.current) dispatch(openSnackbar(messageServeur(error, "Impossible de charger les lieux."), "red"));
    } finally {
      dispatch(loaderOff());
      if (n === requeteListe.current) setChargement(false);
    }
  };

  // ── Approbation d'une sélection (décision D5) ─────────────────────────
  // Plus d'« Approuver tout » : l'admin coche explicitement les lieux, voit le
  // récapitulatif (nombre, Tips crédités) et confirme.
  const [selectionVerifies, setSelectionVerifies] = useState<string[]>([]);
  const lignesSelectionnees = valTab === 1 ? rows.filter(r => selectionVerifies.includes(r.referenceId)) : [];
  const tipsSelection = lignesSelectionnees.reduce((t, r) => t + (r.aCrediter ?? 0), 0);

  useEffect(() => {
    fetchListe();
    // La sélection ne vaut que pour la page affichée.
    setSelectionVerifies([]);
  }, [valTab, page, rowsPerPage, rechercheServeur, verdictFilter, verdictTri]);

  const recharger = () => { fetchListe(); apresDecisionExterne?.(); };

  const basculerSelection = (id: string) =>
    setSelectionVerifies(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);

  const handleApproveSelection = async () => {
    dispatch(loaderOn());
    let reussis = 0;
    for (const r of lignesSelectionnees) {
      const base = { locationType: r.Category, locationName: r.Location_Name, address: r.Location_Address, status: "approved" };
      try {
        // Un POI identifié par placekey passe par la route dédiée, comme l'approbation unitaire.
        if (r.placekey) await updateLocationByPlacekey({ ...base, placekey: r.placekey });
        else await updateLocation({ ...base, referenceId: r.referenceId });
        reussis++;
      } catch (e) { console.error("[approve selection]", r.referenceId, e); }
    }
    setSelectionVerifies([]);
    recharger();
    dispatch(openSnackbar(`${reussis} lieu(x) approuvé(s) sur ${lignesSelectionnees.length}.`, reussis === lignesSelectionnees.length ? "green" : "red"));
    dispatch(loaderOff());
  };

  // ── Masquage (motif obligatoire, conservé au journal) ───────────────────
  const handleLocationDelete = async (id: any) => {
    const jwt = sessionStorage.getItem("jwtToken");
    if (!jwt) { dispatch(openSnackbar("Session introuvable. Veuillez vous reconnecter.", "red")); return; }
    const motif = window.prompt("Motif du masquage (obligatoire, conservé au journal) :");
    if (!motif || !motif.trim()) { dispatch(openSnackbar("Masquage annulé : motif obligatoire.", "red")); return; }
    dispatch(loaderOn());
    try {
      await updateDeleteLocation({ delete: "Yes", referenceId: id }, motif.trim());
      recharger();
    } catch (e: any) {
      dispatch(openSnackbar(e?.response?.status === 404
        ? "Lieu introuvable : il a peut-être déjà été masqué."
        : messageServeur(e, "Masquage refusé par le serveur."), "red"));
    }
    dispatch(loaderOff());
  };

  // Réafficher un lieu masqué : il revient dans la file et sur la carte ;
  // le serveur journalise la décision « demasquage ».
  const handleReafficher = async (id: string | null) => {
    if (!id) return;
    dispatch(loaderOn());
    try {
      await updateDeleteLocation({ delete: "NO", referenceId: id });
      dispatch(openSnackbar("Lieu réaffiché.", "green"));
      recharger();
    } catch (e: any) {
      dispatch(openSnackbar(messageServeur(e, "Réaffichage refusé par le serveur."), "red"));
    }
    dispatch(loaderOff());
  };

  return {
    rows, compteurs, totalOnglet, chargement,
    valTab, setValTab, page, setPage, rowsPerPage, setRowsPerPage,
    searchQuery, setSearchQuery, verdictFilter, setVerdictFilter, verdictTri, setVerdictTri,
    selectionVerifies, basculerSelection, lignesSelectionnees, tipsSelection, handleApproveSelection,
    handleLocationDelete, handleReafficher, recharger,
  };
}
